import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as ella from "./ellaApi";
import { useAuth } from "./auth";

const StoreCtx = createContext(null);

const BRANCH_STORAGE = "ella_branch";

export function StoreProvider({ children }) {
  const { token } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(() => {
    try { return JSON.parse(localStorage.getItem(BRANCH_STORAGE) || "null"); }
    catch { return null; }
  });
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartHydrated, setCartHydrated] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  // Local guest cart (qty by productId) for users without login
  const [guestCart, setGuestCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ella_guest_cart") || "{}"); }
    catch { return {}; }
  });

  // Fetch public catalog data once
  useEffect(() => {
    ella.getBranches().then((res) => {
      if (res.status) {
        setBranches(res.data);
        if (!branch && res.data?.length) {
          // default to Jakarta
          const dflt = res.data.find((b) => b.Name.toLowerCase().includes("jakarta")) || res.data[0];
          setBranch(dflt);
          localStorage.setItem(BRANCH_STORAGE, JSON.stringify(dflt));
        }
      }
    }).catch(() => {});
    ella.getCategories().then((res) => res.status && setCategories(res.data || [])).catch(() => {});
    ella.getBanners().then((res) => res.status && setBanners(res.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  // Payment methods require a session upstream
  useEffect(() => {
    if (!token) { setPaymentMethods([]); return; }
    ella.getPaymentMethods().then((res) => res.status && setPaymentMethods(res.data || [])).catch(() => {});
  }, [token]);

  const changeBranch = (b) => {
    setBranch(b);
    localStorage.setItem(BRANCH_STORAGE, JSON.stringify(b));
  };

  // Refresh cart on auth change
  const refreshCart = useCallback(async () => {
    if (!token) { setCartHydrated(true); return; }
    try {
      const res = await ella.getCart();
      if (res.status) setCart(res.data || []);
    } catch {}
    finally { setCartHydrated(true); }
  }, [token]);

  useEffect(() => { setCartHydrated(false); refreshCart(); }, [refreshCart]);

  const persistGuest = (g) => {
    setGuestCart(g);
    localStorage.setItem("ella_guest_cart", JSON.stringify(g));
  };

  const addToCart = async (product, qty = 1, notes = "") => {
    if (!token) {
      // guest: store locally
      const next = { ...guestCart };
      const existing = next[product.ProductID] || { product, qty: 0, notes: "" };
      existing.qty = Math.max(0, existing.qty + qty);
      existing.notes = notes || existing.notes;
      if (existing.qty === 0) delete next[product.ProductID];
      else next[product.ProductID] = existing;
      persistGuest(next);
      return { ok: true, guest: true };
    }
    // Compute the target qty ourselves and send Source "cart" (= set exact qty):
    // with Source "product" the API would bump an existing row by only +1.
    const existing = cart.find((i) => i.ProductID === product.ProductID);
    const targetQty = Number(existing?.Qty || 0) + qty;
    const res = await ella.saveCartItem({
      productId: product.ProductID,
      qty: targetQty,
      notes: notes || existing?.Notes || "",
      source: "cart",
    });
    await refreshCart();
    return { ok: res.status, message: res.message };
  };

  const updateCartQty = async (productId, qty, notes = "") => {
    if (!token) {
      const next = { ...guestCart };
      if (qty <= 0) delete next[productId];
      else if (next[productId]) next[productId] = { ...next[productId], qty, notes };
      persistGuest(next);
      return { ok: true, guest: true };
    }
    const res = await ella.saveCartItem({ productId, qty, notes, source: "cart" });
    await refreshCart();
    return { ok: res.status, message: res.message };
  };

  // Sync guest cart to server after login (exact quantities)
  const syncGuestCart = useCallback(async () => {
    if (!token) return;
    const items = Object.values(guestCart);
    if (!items.length) return;
    for (const it of items) {
      try {
        await ella.saveCartItem({
          productId: it.product.ProductID,
          qty: it.qty,
          notes: it.notes || "",
          source: "cart",
        });
      } catch {}
    }
    persistGuest({});
    await refreshCart();
  }, [token, guestCart, refreshCart]);

  useEffect(() => { if (token) syncGuestCart(); }, [token]); // eslint-disable-line

  // Compute unified cart view
  const cartItems = token ? cart : Object.values(guestCart).map((g) => ({
    ProductID: g.product.ProductID,
    Product: g.product.Product,
    Price: g.product.Price,
    Qty: g.qty,
    Notes: g.notes,
    ImagePath: g.product.ImagePath,
    BranchID: g.product.BranchID,
    Branch: g.product.Branch,
    Stock: g.product.Stock,
    Discount: g.product.Discount,
    DiscountType: g.product.DiscountType,
  }));

  const cartSubtotal = cartItems.reduce(
    (s, i) => s + ella.effectivePrice(i.Price, i.DiscountType, i.Discount) * Number(i.Qty || 0),
    0
  );

  const cartCount = cartItems.reduce((s, i) => s + Number(i.Qty || 0), 0);

  // Items grouped per branch — delivery is priced per branch, so the cart and
  // checkout present the lines that way too.
  const cartByBranch = (() => {
    const groups = new Map();
    for (const item of cartItems) {
      const key = item.Branch || "Lainnya";
      if (!groups.has(key)) groups.set(key, { branch: key, branchId: item.BranchID, items: [], subtotal: 0 });
      const g = groups.get(key);
      g.items.push(item);
      g.subtotal += ella.effectivePrice(item.Price, item.DiscountType, item.Discount) * Number(item.Qty || 0);
    }
    return [...groups.values()].sort((a, b) => a.branch.localeCompare(b.branch));
  })();

  return (
    <StoreCtx.Provider value={{
      branches, branch, changeBranch,
      categories, banners, paymentMethods,
      cartItems, cartByBranch, cartSubtotal, cartCount, cartHydrated,
      addToCart, updateCartQty, refreshCart,
    }}>
      {children}
    </StoreCtx.Provider>
  );
}

export const useStore = () => useContext(StoreCtx);
